import { PropsWithChildren, useLayoutEffect, useRef } from "react";

import "./Masonry.css";

interface MasonryGridProps extends PropsWithChildren {
  className?: string;
}

interface MasonryItemProps extends PropsWithChildren {
  className?: string;
}

const joinClassNames = (...classNames: Array<string | undefined | false>) => {
  return classNames.filter(Boolean).join(" ");
};

export const MasonryGrid = ({ children, className }: MasonryGridProps) => {
  return <div className={joinClassNames("masonry-grid", className)}>{children}</div>;
};

export const MasonryItem = ({ children, className }: MasonryItemProps) => {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const item = itemRef.current;
    const content = contentRef.current;

    if (!item || !content) return;

    let animationFrameId: number | null = null;

    const updateSpan = () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        const grid = item.parentElement;

        if (!grid) return;

        const gridStyles = window.getComputedStyle(grid);

        const rowHeight = Number.parseFloat(gridStyles.gridAutoRows) || 8;

        const rowGap = Number.parseFloat(gridStyles.rowGap) || 0;

        const contentHeight = content.getBoundingClientRect().height;

        const span = Math.max(1, Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap)));

        item.style.gridRowEnd = `span ${span}`;
      });
    };

    updateSpan();

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateSpan) : null;

    resizeObserver?.observe(content);

    window.addEventListener("resize", updateSpan);

    return () => {
      resizeObserver?.disconnect();

      window.removeEventListener("resize", updateSpan);

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div ref={itemRef} className={joinClassNames("masonry-item", className)}>
      <div ref={contentRef} className="masonry-item-content">
        {children}
      </div>
    </div>
  );
};
